import { cloneDeep } from 'lodash';
import { useCallback } from 'react';
import { ScratchpadRecommendation } from '../../../../../../../api/graphql/generated';
import { getDifferentKeyPaths } from '../../../../../../../util/object';
import { ApplicationDefaultParameterSetLocalFormikFormValues } from '../../../../../shared/component/settings/application-settings-edit';
import {
  Flow,
  FlowItem,
  FlowItemForm,
  FlowItemParameterRef,
  SystemUnit,
  WidgetType,
} from '../../../../../shared/model/flow.types';
import {
  FlowItemService,
  FlowService,
} from '../../../../../shared/service/service.types';
import { getParameterValue } from '../../../../../shared/util/flow-parameter.util';
import { BeaconCuttingData } from '../../../model/beacon-cutting-data';
import { CutSettingsRecommendation } from './settings-recommendation.hook';

/**
 * This will find if there are differences between two forms by using the passed
 * in flow item parameters and tags. It will return if it meets one
 * of the criteria.
 */
function findFormDifferences<T extends Record<string, unknown> = FlowItemForm>(
  previousForm: T,
  updatedForm: T,
  params?: FlowItemParameterRef[],
  tags?: string[]
): boolean {
  const diff = getDifferentKeyPaths(previousForm, updatedForm);
  let isDifferent = false;

  // check if the changed value should trigger a recommendation
  for (const path of diff) {
    if (params) {
      // the path in the diff should contain one of the setting or param names
      const matchedParam = params.find((param) => {
        // we're adding "value" to the path to only check if the value changed
        // not the "originalValue"
        const pattern = new RegExp(`${param}.value`, 'g');
        const match = path.match(pattern);
        return match !== null;
      });

      if (matchedParam) {
        isDifferent = true;
        break;
      }
    }

    if (tags) {
      const matchedConfigKey = tags.find((key) => {
        const pattern = new RegExp(key, 'g');
        const match = path.match(pattern);
        return match !== null;
      });

      if (matchedConfigKey) {
        isDifferent = true;
        break;
      }
    }
  }

  return isDifferent;
}

interface IsRecommendationInputEqualToOptions {
  flowItemService: FlowItemService<BeaconCuttingData.FlowConfig>;
  flowService: FlowService<BeaconCuttingData.FlowConfig>;
  recommendation: ScratchpadRecommendation;
  settingsForm: ApplicationDefaultParameterSetLocalFormikFormValues;
  system: SystemUnit;
}

const compareRecommendationInput = (
  options: IsRecommendationInputEqualToOptions
) => {
  const { flowItemService, flowService, recommendation, settingsForm, system } =
    options;

  const incomingRecommendationForm = flowItemService.convertFlowItemToForm(
    recommendation.inputs.flowItem,
    system
  );

  const incomingRecommendationTags =
    flowService.mapFlowConfigToApplicationDefaultParameterSetFormTags({
      config: recommendation.inputs.flowConfig,
    } as Flow<BeaconCuttingData.FlowConfig>);

  const convertedIncomingRecommendationForm =
    convertFormParametersToNumbersFromString(
      incomingRecommendationForm,
      flowItemService
    );

  const convertedSettingsFlowItemForm =
    convertFormParametersToNumbersFromString(
      settingsForm.data,
      flowItemService
    );

  const incomingRecommendationInputDifferent =
    findFormDifferences<ApplicationDefaultParameterSetLocalFormikFormValues>(
      {
        data: convertedIncomingRecommendationForm,
        tags: incomingRecommendationTags,
      },
      { data: convertedSettingsFlowItemForm, tags: settingsForm.tags },
      undefined,
      BeaconCuttingData.RECOMMENDATION_CONFIG_INPUTS
    );

  return !incomingRecommendationInputDifferent;
};

// forms require the numeric parameters to be strings...
// so this function clones the form and returns them back
// to numbers
const convertFormParametersToNumbersFromString = <FlowConfig,>(
  form: FlowItemForm,
  flowItemService: FlowItemService<FlowConfig>
) => {
  const convertedForm = cloneDeep(form);
  for (const [k, v] of Object.entries(convertedForm.parameters)) {
    if (
      Object.values<string>(BeaconCuttingData.FlowItemParameterRef).includes(k)
    ) {
      const metadata = flowItemService.getParameterMetadata(
        BeaconCuttingData.FlowItemRef.CUT,
        k as FlowItemParameterRef
      );

      const metaTypeIsNumber =
        metadata && metadata.value.uom && metadata.value.type === 'number';

      const widgetTypeIsNumber =
        metadata?.ui?.widget && metadata.ui.widget === WidgetType.NUMBER;

      if (metaTypeIsNumber || widgetTypeIsNumber) {
        try {
          const convertedValue = Number(v.value);
          if (!isNaN(convertedValue)) {
            convertedForm.parameters[k].value = convertedValue;
          }
        } catch {
          // if there's an error converting for whatever reason, leave alone
          convertedForm.parameters[k].value = v.value;
        }
      }
    }
  }

  return convertedForm;
};

interface IsRecommendationUtilizedOptions {
  recommendation: CutSettingsRecommendation;
  flowItem: FlowItem;
}

const isRecommendationUtilized = (
  options: IsRecommendationUtilizedOptions
): boolean => {
  const { recommendation, flowItem } = options;

  const unusedRecommendation = recommendation.parameters.find(
    (recommendedParam) => {
      const actualValue = getParameterValue<number>(
        flowItem,
        recommendedParam.ref
      );

      const { minValue, maxValue, value } = recommendedParam;

      if (minValue && actualValue < minValue) {
        return true;
      }

      if (maxValue && actualValue > maxValue) {
        return true;
      }

      // TODO with conversions, these values can differ by precision
      return actualValue != value;
    }
  );

  return unusedRecommendation === undefined;
};

interface GetFlowRecommendationOptions {
  recommendationId?: string;
}

type GetFlowRecommendationReturnType = (
  options: GetFlowRecommendationOptions
) => Flow['recommendation'] | undefined;

const useGetFlowRecommendation = (): GetFlowRecommendationReturnType => {
  const getFlowRecommendation = useCallback(
    (
      options: GetFlowRecommendationOptions
    ): Flow['recommendation'] | undefined => {
      const { recommendationId } = options;

      if (recommendationId) {
        return { id: recommendationId, utilized: true };
      }
    },
    []
  );

  return getFlowRecommendation;
};

export {
  compareRecommendationInput,
  convertFormParametersToNumbersFromString,
  findFormDifferences,
  isRecommendationUtilized,
  useGetFlowRecommendation,
};
